import { useMemo } from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

type UrlObject = {
  pathname?: string | null
  query?: Record<string, unknown> | URLSearchParams
}
type RouterHref = string | UrlObject

type RouterQuery = Record<string, string | string[] | undefined>

export interface CompatRouter {
  pathname: string
  asPath: string
  query: RouterQuery
  isReady: boolean
  push: (href: RouterHref, as?: unknown, options?: { shallow?: boolean; scroll?: boolean }) => void
  replace: (href: RouterHref, as?: unknown, options?: { shallow?: boolean; scroll?: boolean }) => void
}

function serializeQuery(q: UrlObject['query']): string {
  if (!q) return ''
  if (q instanceof URLSearchParams) {
    const s = q.toString()
    return s ? `?${s}` : ''
  }
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) {
    if (v == null) continue
    if (Array.isArray(v)) v.forEach((vv) => params.append(k, String(vv)))
    else params.append(k, String(v))
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

function buildUrl(href: RouterHref): string {
  if (typeof href === 'string') return href
  const path = href.pathname || ''
  return `${path}${serializeQuery(href.query)}`
}

export function useRouter(): CompatRouter {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const params = useParams()

  return useMemo<CompatRouter>(() => {
    const query: RouterQuery = {}
    searchParams.forEach((value, key) => {
      const existing = query[key]
      if (existing === undefined) {
        query[key] = value
      } else if (Array.isArray(existing)) {
        existing.push(value)
      } else {
        query[key] = [existing, value]
      }
    })
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) query[k] = v as string
    }
    return {
      pathname: location.pathname,
      asPath: `${location.pathname}${location.search}${location.hash}`,
      query,
      isReady: true,
      push: (href) => navigate(buildUrl(href)),
      replace: (href) => navigate(buildUrl(href), { replace: true }),
    }
  }, [location, navigate, searchParams, params])
}